// Meta rejects an id with any stray character around it — `fbq('init', '123 ')` logs
// "Invalid PixelID: null" and registers nothing, so every later track() call is dropped.
const PIXEL_ID = /^\d{10,20}$/
const INVISIBLE = /[\s ​-‏‪-‮﻿]/g

// Sellers paste one of three things into the pixel field: the bare id, the id with
// whitespace/RTL marks around it, or the entire Meta snippet. Recover an id from any of
// them, and return null rather than injecting a value Meta will reject.
export const sanitizePixelId = (raw) => {
    if (typeof raw !== 'string') return null

    if (/fbq|<script|facebook\.com\/tr/i.test(raw)) {
        const fromInit = raw.match(/fbq\(\s*['"]init['"]\s*,\s*['"](\d{10,20})['"]/i)
        if (fromInit) return fromInit[1]
        // Require a boundary after the digits so an overlong id (…?id=<21+ digits>) is
        // rejected, not silently truncated to its first 20 digits and saved as a wrong id.
        // `>` is included so an unquoted attribute (<img src=…?id=123>) still resolves.
        const fromUrl = raw.match(/[?&]id=(\d{10,20})(?=[&#'"\s>]|$)/i)
        if (fromUrl) return fromUrl[1]
        return null
    }

    const cleaned = raw.replace(INVISIBLE, '')
    return PIXEL_ID.test(cleaned) ? cleaned : null
}

// Navigation between the seller page and event pages is client-side, so one browser session
// can initialise several pixels (the seller's, then an event's). `fbq('track', …)` sends to
// EVERY initialised pixel; `fbq('trackSingle', id, …)` sends to one. Every event here goes to
// the pixel of the page the visitor is on — the one most recently passed to init().
let activePixelId = null
const initialisedPixels = new Set()

// Returns whether the call reached a pixel loader at all.
const fbq = (...args) => {
    try {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
            window.fbq(...args)
            return true
        }
    } catch (e) {
        console.error(e)
    }
    return false
}

// Meta's standard bootstrap: a queueing stub on window.fbq plus the async fbevents.js load.
const loadPixelScript = () => {
    if (typeof window === 'undefined' || window.fbq || typeof document === 'undefined') return
    const n = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!window._fbq) window._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = document.createElement('script')
    t.async = true
    t.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const s = document.getElementsByTagName('script')[0]
    if (s && s.parentNode) s.parentNode.insertBefore(t, s)
    else if (document.head) document.head.appendChild(t)
}

// Make `rawId` the page's pixel (registering it with Meta the first time). Returns the clean
// id, or null (and drops later events) when the page has no usable pixel — never fall through
// to whatever pixel a previous page loaded. Counting the page view is pageView()'s job.
const init = (rawId) => {
    const pixelId = sanitizePixelId(rawId)
    activePixelId = pixelId
    if (!pixelId) return null
    try {
        loadPixelScript()
    } catch (e) {
        console.error(e)
    }
    // Only remember the registration if a loader actually received it; otherwise retry next time.
    if (!initialisedPixels.has(pixelId) && fbq('init', pixelId)) {
        initialisedPixels.add(pixelId)
    }
    return pixelId
}

const pageView = () => {
    if (!activePixelId) return
    fbq('trackSingle', activePixelId, 'PageView')
}

// `options.eventID` lets Meta deduplicate the event (and match a future server-side copy).
const track = (eventName, params, options) => {
    if (!activePixelId) return
    if (options) {
        fbq('trackSingle', activePixelId, eventName, params, options)
    } else {
        fbq('trackSingle', activePixelId, eventName, params)
    }
}

const _resetForTests = () => {
    activePixelId = null
    initialisedPixels.clear()
}

export default {
    init,
    pageView,
    track,
    _resetForTests,
}
